import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleAuthCallback, loading, directLogin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // Add a ref to track if the callback has been processed
  const callbackProcessedRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Check if callback has already been processed
      if (callbackProcessedRef.current) {
        console.log('AuthCallback: Callback already processed, skipping');
        return;
      }
      
      console.log('✅ AuthCallback: Starting new callback processing');
      console.log('✅ CURRENT URL:', window.location.href);
      
      // Mark callback as processed to prevent duplicate requests
      callbackProcessedRef.current = true;
      
      try {
        console.log('AuthCallback: Starting authentication callback processing');
        console.log('Current location:', location);
        
        // Check for test token in URL (from test login flow)
        const params = new URLSearchParams(location.search);
        const testToken = params.get('token');
        const userId = params.get('user_id');
        const code = params.get('code');
        const state = params.get('state');
        
        console.log('✅ Auth params from URL:', { testToken, userId, code: code ? `${code.substring(0, 10)}...` : null, state });
        
        if (testToken && userId) {
          console.log('Processing test authentication...');
          try {
            // This is a test authentication flow
            const userIdNum = parseInt(userId);
            
            // No need to save token to localStorage or set API headers 
            // Cookies are handled by the server
            console.log('Test auth: using cookie-based authentication');
            
            // Use directLogin to set up the authentication state
            console.log('Calling directLogin with user ID:', userIdNum);
            await directLogin(userIdNum);
            console.log('directLogin completed successfully');
            
            // Navigate to the shifts page
            console.log('Navigating to /myshifts');
            navigate('/myshifts', { replace: true });
          } catch (err: any) {
            console.error('Error during test authentication:', err);
            setError(`Test auth error: ${err.message || 'Unknown error'}`);
            // Still try to navigate to shifts page
            setTimeout(() => navigate('/myshifts', { replace: true }), 1000);
          }
          return;
        }
        
        // Handle regular Keycloak callback
        console.log('✅ No test token found, handling regular Keycloak callback with code:', code ? `${code.substring(0, 10)}...` : 'none');
        
        if (!code) {
          console.error('✅ ERROR: No authorization code found in URL!');
          setError('No authorization code found in URL. Cannot complete authentication.');
          return;
        }
        
        console.log('✅ About to call handleAuthCallback...');
        await handleAuthCallback();
        console.log('✅ Keycloak auth completed successfully! Navigating to /myshifts');
        navigate('/myshifts', { replace: true });
      } catch (error: any) {
        console.error('✅ CRITICAL ERROR in auth callback:', error);
        console.error('✅ Error details:', error.message);
        if (error.response) {
          console.error('✅ Error response:', error.response.data);
        }
        
        setError(`Auth callback error: ${error.message || 'Unknown error'}`);
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      }
    };

    handleCallback();
    
    // Cleanup function to prevent memory leaks
    return () => {
      // No cleanup needed, but this ensures React doesn't complain
    };
    
  }, [handleAuthCallback, navigate, location, directLogin]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-text-red-600">
              Authentication Error
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {error}
            </p>
            <p className="mt-4">
              <button 
                onClick={() => navigate('/login')}
                className="px-4 py-2 bg-blue-500 text-white rounded"
              >
                Return to Login
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Completing authentication...
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Please wait while we complete your login.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">
            Authentication Successful
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Redirecting to your shifts...
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback; 